import { Button, Size, ControlType, Priority } from '@transferwise/components';
import { formatMoney } from '@transferwise/formatting';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import DeliveryEstimation from '../../common/DeliveryEstimation';
import ExchangeRateIndicator from '../../common/ExchangeRateIndicator';
import Label from '../../common/Label';
import { isWiseAlias } from '../../resources/aliasFilter';
import createClassNamesFromStyles from '../../resources/createClassNamesFromStyles';
import createUrl from '../../resources/createUrl';
import generateUrl, { AvailablePages } from '../../resources/generateUrl';
import isConsideredMidmarketRate from '../../resources/isConsideredMidmarketRate';
import style from '../TravelMoney.module.less';

const createClassNames = createClassNamesFromStyles(style);

const ProviderRow = ({
  sourceCurrency,
  targetCurrency,
  name,
  alias,
  logos,
  midMarketRate,
  deliveryEstimation,
  receivedAmount,
  rate,
  feeBreakdown,
  twSavings,
  ctaUrlSavings,
  ctaUrlNotCheapest,
  track,
}) => {
  const { formatMessage, locale } = useIntl();
  const isTW = isWiseAlias(alias);

  const deliveryFee = feeBreakdown.find((f) => f.feeType === 'DELIVERY')?.feeAmount;

  const MID_MARKET_URL = createUrl(
    generateUrl(locale, 'mid-market-rate/', AvailablePages.MID_MARKET_RATE),
  );
  return (
    <>
      <tr className={style['table-plain__row']}>
        {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
        <td className={createClassNames(['table-plain__cell', 'table-plain__cell--provider-logo'])}>
          <div className={createClassNames(['providerLogoContainer'])}>
            {logos && (
              <img
                className={createClassNames(['providerLogo'])}
                src={logos?.normal?.pngUrl}
                alt={formatMessage(
                  {
                    id: 'provider-logo-alt-text',
                  },
                  { name },
                )}
              />
            )}
          </div>
        </td>
        <td
          className={createClassNames(['table-plain__cell'])}
          data-label={formatMessage({ id: 'exchange-rate' })}
        >
          {/* TODO: This ExchangeRateIndicator component with midmarket rate label should be extracted out */}
          <ExchangeRateIndicator
            referenceRate={midMarketRate}
            rate={rate}
            label={
              isConsideredMidmarketRate(rate, midMarketRate) ? (
                <Label message="mid-market-rate" link={MID_MARKET_URL} target="_blank" />
              ) : null
            }
            sourceCurrency={sourceCurrency}
            targetCurrency={targetCurrency}
          />
        </td>
        <td
          className={
            isTW
              ? createClassNames(['table-plain__cell', 'one-off-fee'])
              : createClassNames(['table-plain__cell'])
          }
          data-label={
            isTW
              ? formatMessage({ id: 'delivery-fee-one-off' })
              : formatMessage({ id: 'delivery-fee' })
          }
        >
          <span className={createClassNames(['delivery-fee'])}>
            {formatMoney(deliveryFee, sourceCurrency, locale, {
              alwaysShowDecimals: true,
            })}
            {isTW && (
              <small className={createClassNames(['text-success', 'm-l-1'])}>
                <FormattedMessage id="one-off-fee" />
              </small>
            )}
          </span>
        </td>
        <td
          className={style['table-plain__cell']}
          data-label={formatMessage({ id: 'should-arrive' })}
        >
          {deliveryEstimation && (
            <DeliveryEstimation
              alias={alias}
              onClickingDeliveryEstimationTooltip={() => {}}
              deliveryEstimation={deliveryEstimation}
              isPartner={false}
              styles={style}
              isTravelMoney
            />
          )}
        </td>
        <td
          className={createClassNames([
            'table-plain__cell',
            'featured--last-cell',
            'table-plain__cell--will-cost-you',
          ])}
          data-label={formatMessage({ id: 'will-cost-you' })}
        >
          <span className={createClassNames(['featured-amount'])}>
            {formatMoney(receivedAmount, targetCurrency, locale, {
              alwaysShowDecimals: true,
            })}
          </span>
        </td>
      </tr>

      {isTW && (
        <SavingsRow
          savings={twSavings}
          currency={sourceCurrency}
          ctaUrlSavings={ctaUrlSavings}
          ctaUrlNotCheapest={ctaUrlNotCheapest}
          track={track}
        />
      )}
    </>
  );
};

ProviderRow.propTypes = {
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  alias: PropTypes.string.isRequired,
  logo: PropTypes.string,
  deliveryEstimation: PropTypes.shape(DeliveryEstimation.propTypes.deliveryEstimation).isRequired,
  rate: PropTypes.number.isRequired,
  midMarketRate: PropTypes.number,
  receivedAmount: PropTypes.number.isRequired,
  feeBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      feeType: PropTypes.string.isRequired,
      feeAmount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  twSavings: PropTypes.number.isRequired,
  ctaUrlSavings: PropTypes.string.isRequired,
  ctaUrlNotCheapest: PropTypes.string.isRequired,
  track: PropTypes.func,
};

ProviderRow.defaultProps = {
  logo: '',
  midMarketRate: 0,
  track: () => {},
};

const SavingsRow = ({ savings, currency, ctaUrlSavings, ctaUrlNotCheapest, track }) => {
  const { locale } = useIntl();
  const showSavings = savings > 0;
  return (
    <tr className={createClassNames(['row-cta'])}>
      <th className={createClassNames(['table-plain__cell', 'row-cta__text'])} colSpan="4">
        {showSavings ? (
          <FormattedMessage
            values={{
              savings: formatMoney(savings, currency, locale, {
                alwaysShowDecimals: true,
              }),
            }}
            id="savings"
          />
        ) : (
          <FormattedMessage id="not-cheapest" />
        )}
      </th>
      <th
        className={createClassNames([
          'table-plain__cell',
          'featured--last-cell',
          'table-plain__cell--will-cost-you',
        ])}
      >
        <a
          className={createClassNames(['send-with-tw-cta'])}
          href={showSavings ? ctaUrlSavings : ctaUrlNotCheapest}
        >
          <Button
            size={Size.SMALL}
            type={ControlType.POSITIVE}
            priority={Priority.PRIMARY}
            className={createClassNames(['send-with-tw-cta'])}
            onClick={
              showSavings
                ? () => track('Travel Money - See Savings')
                : () => track('Travel Money - Get Card')
            }
          >
            {showSavings ? (
              <FormattedMessage id="see-savings" />
            ) : (
              <FormattedMessage id="get-the-card" />
            )}
          </Button>
        </a>
      </th>
    </tr>
  );
};

SavingsRow.propTypes = {
  currency: PropTypes.string.isRequired,
  savings: PropTypes.number.isRequired,
  ctaUrlSavings: PropTypes.string.isRequired,
  ctaUrlNotCheapest: PropTypes.string.isRequired,
  track: PropTypes.func.isRequired,
};

export default ProviderRow;
